import { useState } from "react";
import {
  Star,
  Eye,
  CheckCircle,
  XCircle,
  Rocket,
  FunnelSimple,
} from "@phosphor-icons/react";
import { ScrollArea } from "../ui/scroll-area";
import { getFilterButtonClass, getFilterTextClass, getFilterIconClass, getCardClass, getTextPrimary, getTextSecondary, getBorderClass, getDividerClass } from "../../lib/styleHelpers";
import { WatchlistItem } from "./WatchlistItem";
import { WatchlistEmptyState } from "./WatchlistEmptyState";

const STATUS_CONFIG = {
  watching: { label: "Watching", icon: Eye, color: "text-blue-500 bg-blue-500/10 border-blue-500/30", colorLight: "text-blue-600 bg-blue-50 border-blue-200" },
  ready_to_execute: { label: "Ready to Execute", icon: Rocket, color: "text-amber-400 bg-amber-500/10 border-amber-500/30", colorLight: "text-amber-600 bg-amber-50 border-amber-200" },
  executed: { label: "Executed", icon: CheckCircle, color: "text-emerald-400 bg-emerald-500/10 border-emerald-500/30", colorLight: "text-emerald-600 bg-emerald-50 border-emerald-200" },
  passed: { label: "Passed", icon: XCircle, color: "text-gray-400 bg-gray-500/10 border-gray-500/30", colorLight: "text-gray-500 bg-gray-100 border-gray-200" },
};

const FILTER_BUTTONS = [
  { key: "all", label: "All", icon: Star },
  { key: "watching", label: "Watching", icon: Eye },
  { key: "ready_to_execute", label: "Ready", icon: Rocket },
  { key: "executed", label: "Executed", icon: CheckCircle },
  { key: "passed", label: "Passed", icon: XCircle },
];

function getProbColor(score) {
  if (score >= 0.8) return "#10b981";
  if (score >= 0.7) return "#a855f7";
  if (score >= 0.6) return "#f59e0b";
  return "#ef4444";
}

export function WatchlistTab({
  watchlist,
  onUpdateItem,
  onRemoveItem,
  onViewOpportunity,
  onTrackInPortfolio,
  portfolioOppIds,
  isDark,
}) {
  const [filter, setFilter] = useState("all");
  const [editingId, setEditingId] = useState(null);
  const [editNotes, setEditNotes] = useState("");

  const filtered =
    filter === "all"
      ? watchlist
      : watchlist.filter((item) => item.status === filter);

  const counts = {
    all: watchlist.length,
    watching: watchlist.filter((i) => i.status === "watching").length,
    ready_to_execute: watchlist.filter((i) => i.status === "ready_to_execute").length,
    executed: watchlist.filter((i) => i.status === "executed").length,
    passed: watchlist.filter((i) => i.status === "passed").length,
  };

  const startEditNotes = (item) => {
    setEditingId(item.id);
    setEditNotes(item.notes || "");
  };

  const saveNotes = (itemId) => {
    onUpdateItem(itemId, { notes: editNotes });
    setEditingId(null);
  };

  return (
    <div className="animate-fade-in" data-testid="watchlist-content">
      {/* Summary Cards */}
      <div className="grid grid-cols-2 md:grid-cols-5 gap-3 mb-6">
        {FILTER_BUTTONS.map(({ key, label, icon: Icon }) => (
          <button
            key={key}
            onClick={() => setFilter(key)}
            className={`p-3 rounded-xl border text-left transition-all ${getFilterButtonClass(filter === key, isDark)}`}
            data-testid={`watchlist-filter-${key}`}
          >
            <div className="flex items-center gap-2 mb-1">
              <Icon size={16} className={getFilterIconClass(filter === key, isDark)} />
              <span className={`text-xs font-body ${getTextSecondary(isDark)}`}>{label}</span>
            </div>
            <p className={`text-xl font-bold ${getFilterTextClass(filter === key, isDark)}`}>
              {counts[key]}
            </p>
          </button>
        ))}
      </div>

      {/* Watchlist Items */}
      <div className={`rounded-xl border ${getCardClass(isDark)}`}>
        <div className={`p-4 border-b flex items-center justify-between ${getBorderClass(isDark)}`}>
          <div>
            <h2 className={`text-lg font-bold ${getTextPrimary(isDark)}`}>
              <Star size={20} weight="fill" className="inline text-amber-400 mr-2" />
              My Watchlist
            </h2>
            <p className={`text-sm font-body ${getTextSecondary(isDark)}`}>
              {filtered.length} opportunit{filtered.length === 1 ? "y" : "ies"}
              {filter !== "all" ? ` (${STATUS_CONFIG[filter]?.label})` : ""}
            </p>
          </div>
          <div className="flex items-center gap-2">
            <FunnelSimple size={16} className={getTextSecondary(isDark)} />
            <span className={`text-xs ${getTextSecondary(isDark)}`}>
              {filter === "all" ? "Showing all" : STATUS_CONFIG[filter]?.label}
            </span>
          </div>
        </div>

        {filtered.length === 0 ? (
          <WatchlistEmptyState filter={filter} statusConfig={STATUS_CONFIG} isDark={isDark} />
        ) : (
          <ScrollArea className="max-h-[600px]">
            <div className={`divide-y ${getDividerClass(isDark)}`}>
              {filtered.map((item) => (
                <WatchlistItem
                  key={item.id}
                  item={item}
                  editingId={editingId}
                  editNotes={editNotes}
                  setEditNotes={setEditNotes}
                  saveNotes={saveNotes}
                  setEditingId={setEditingId}
                  startEditNotes={startEditNotes}
                  onViewOpportunity={onViewOpportunity}
                  onUpdateItem={onUpdateItem}
                  onRemoveItem={onRemoveItem}
                  onTrackInPortfolio={onTrackInPortfolio}
                  portfolioOppIds={portfolioOppIds}
                  getProbColor={getProbColor}
                  isDark={isDark}
                />
              ))}
            </div>
          </ScrollArea>
        )}
      </div>
    </div>
  );
}
